import { TypeChecker } from '@attain-apps/type-guards'

import type { DocumentNumber } from '../document-number'
import type * as payment from '../payment'

import type { Row } from './row'

/**
 * Generated justification text by document/payment identifier.
 *
 * This is the shape a workbook writer needs: it populates cells by looking up
 * the row it is on, so it wants a keyed lookup rather than the ordered rows the
 * reviewer reads.
 */
export type Lookup = Readonly<Record<payment.Identifier, Lookup.Entry>>
export namespace Lookup {
  export interface Entry {
    readonly key: payment.Identifier
    readonly text: string
    readonly documentNumber?: DocumentNumber
  }
  export namespace Entry {
    export const isEntry: TypeChecker<Entry> = TypeChecker.Factory.object({
      documentNumber: TypeChecker.Factory.optional(TypeChecker.isString),
      key: TypeChecker.isString,
      text: TypeChecker.isString,
    })
  }

  export const isLookup: TypeChecker<Lookup> = TypeChecker.Factory.record(
    TypeChecker.isString,
    Entry.isEntry,
  )

  /**
   * Indexes the rows that produced justification text.
   *
   * Rows without text — an expired award, or a document with nothing to request
   * — are left out, so a writer populating cells skips them rather than
   * clearing whatever a reviewer put there.
   */
  export function fromRows(rows: readonly Row[]): Lookup {
    const lookup: Record<payment.Identifier, Entry> = {}
    for (const row of rows) {
      if (row.text === undefined) {
        continue
      }
      lookup[row.key] = {
        documentNumber: row.documentNumber,
        key: row.key,
        text: row.text,
      }
    }
    return lookup
  }
}
